import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Heading, Input, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation } from "react-router-dom";
import { HttpStatusCode } from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE, NOMINATION_STATUS, VESSEL_STATUS } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectMarketType from "@/components/Selects/SelectMarketType";
import SelectInspector from "@/components/Selects/SelectInspector";
import SelectShip from "@/components/Selects/SelectShip";
import SelectInspectionFee from "@/components/Selects/SelectInspectionFee";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import SelectCompanyFullType from "@/components/Selects/SelectCompanyFullType";
import SelectAgent from "@/components/Selects/SelectAgent";
import Loader from "@/components/Layouts/Loader";
import ModalAddNewVessel from "@/components/Modals/ModalAddNewVessel";
import SelectVesselType from "@/components/Selects/SelectVesselType";
import SelectOperationType from "@/components/Selects/SelectOperationType";
import { inspectionNominationSchema } from "@/schemas/inspectionNominationSchema";
import { getDataInspectorByType } from "@/services/inspectorService";
import { getInspectorFees } from "@/services/inspectionFeeService";
import { getDataVessel, postVessel } from "@/services/vesselService";
import { getDataRef } from "@/services/refDataService";
import { getDataAgent } from "@/services/agentService";
import { getInspectorPersonData } from "@/services/inspectorPersonService";
import { getNominationById, Nomination, postNomination, putNomination } from "@/services/nominationService";
import { parseCurrencyFloat } from "@/utils/currency";
import { Option } from "@/utils/select";
import { Vessel } from "@/services/shipOwnerService";

const EditNomination = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isOpenVessel, onOpen: onOpenVessel, onClose: onCloseVessel } = useDisclosure();
    const toast = useToast();
    const location = useLocation();

    const { id } = location.state || {};

    const [confirmationText, setConfirmationText] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [dataAddVessel, setDataAddVessel] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [listInspectorCompany, setListInspectorCompany] = useState<Option[]>([]);
    const [listInspectorPerson, setListInspectorPerson] = useState<Option[]>([]);
    const [listRawInspector, setListRawInspector] = useState<any[]>([]);
    const [listRawShipName, setListRawShipName] = useState<Vessel[]>([]);
    const [listShipName, setListShipName] = useState<Option[]>([]);
    const [listRawAgent, setListRawAgent] = useState<any[]>([]);
    const [listInspectionFee, setListInspectionFee] = useState<Option[]>([]);
    const [listRawInspectionFee, setListRawInspectionFee] = useState<any[]>([]);
    const [listOperations, setListOperations] = useState<Option[]>([]);
    const [listTypeOfVessels, setListTypeOfVessels] = useState<Option[]>([]);
    const [listMarket, setListMarket] = useState<Option[]>([]);
    const [listSireType, setListSireType] = useState<Option[]>([]);
    const [listCargo, setListCargo] = useState<Option[]>([]);
    const [listMeansOfAccess, setListMeansOfAccess] = useState<Option[]>([]);
    const [listAgent, setListAgent] = useState<Option[]>([]);
    const [selectedRawData, setSelectedRawData] = useState<any>(null);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [prefix, setPrefix] = useState('');
    const [isAgentDisabled, setIsAgentDisabled] = useState(false);
    const [dataDetail, setDataDetail] = useState<Nomination | null>(null);
    const [defaultType, setDefaultType] = useState<string>('');
    const [defaultCompanyInspectionAddressDetail, setDefaultCompanyInspectionAddressDetail] = useState('');
    const [defaultCompanyInspectionPhoneDetail, setDefaultCompanyInspectionPhoneDetail] = useState('');
    const [defaultRawAgent, setDefaultRawAgent] = useState<any>(null);
    const [isFirstEffectDone, setIsFirstEffectDone] = useState(false);
    const [defaultRawVesselIdentifier, setDefaultRawVesselIdentifier] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [isApprovedBoth, setApprovedBoth] = useState(false);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationText("Are you sure you want to cancel?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleCancelVessel = () => {
        onOpen();
        setTitle("Cancel Adding Vessel?");
        setConfirmationText("Are you sure you want to cancel?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Inspection Nomination?");
        setConfirmationText("Are you sure you want to save?");
        setConfirmButtonText("Save Nomination");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const handleSubmitVessel = async (data: any) => {
        setTitle("Add Vessel?");
        setConfirmationText("Are you sure you want to add?");
        setConfirmButtonText("Add Vessel");
        onOpen();
        setDataAddVessel(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const vesselData = listRawShipName?.find((x: any) => x.vesselId === dataEdit.nameOfVessel);

        const obj = {
            id,
            sire: dataEdit.companyType.includes('SIRE') ? true : false,
            bire: dataEdit.companyType.includes('BIRE') ? true : false,
            vetting: dataEdit.companyType.includes('Vetting') ? true : false,
            inspectingCompanyId: dataEdit.companyName,
            companyName: selectedRawData?.inspectorName || dataDetail?.companyName || '',
            inspectionDate: dataEdit.inspectionDate ? format(dataEdit.inspectionDate, FORMAT_ADD_DATE) : null,
            country: dataEdit.location,
            requestDate: dataEdit.requestDate ? format(dataEdit.requestDate, FORMAT_ADD_DATE) : null,
            market: dataEdit.market,
            type: dataEdit.nominationType,
            //agentId: dataEdit.agent,
            agentName: dataEdit.agentName,
            agentOfficePhone: dataEdit.agentOfficePhone,
            agentMobilePhone: dataEdit.agentMobilePhone,
            agentFax: dataEdit.agentFax,
            agentEmail: dataEdit.agentEmailAddress,
            vesselId: dataEdit.nameOfVessel,
            shipOperatorId: vesselData?.shipOperatorId || '',
            shipOperatorName: vesselData?.companyName || '',
            typeOfvessel: dataEdit.typeOfVessel,
            vesselETA: dataEdit.ETAVessel ? format(dataEdit.ETAVessel, FORMAT_ADD_DATE) : null,
            portOfBerth: dataEdit.portOfBerth,
            countryOfInspection: dataEdit.countryOfInspection,
            portOfInspection: dataEdit.portOfInspection,
            eta: dataEdit.ETA ? format(dataEdit.ETA, FORMAT_ADD_DATE) : null,
            etb: dataEdit.ETB ? format(dataEdit.ETB, FORMAT_ADD_DATE) : null,
            etd: dataEdit.ETD ? format(dataEdit.ETD, FORMAT_ADD_DATE) : null,
            operation: dataEdit.operation,
            cargo: dataEdit.cargo,
            meansOfAccess: dataEdit.meansOfAccess,
            inspectorId: dataEdit.inspectorName,
            codeBook: dataEdit.codeBook,
            inspectionFeeId: dataEdit.groupLocation,
            fee: dataEdit.fee,
            awaitingDaysFee: dataEdit.awaitingDaysFee ? parseCurrencyFloat(dataEdit.awaitingDaysFee) : 0,
            nameofVesselBire: dataEdit.nameofVesselBire || '',
            vesselIdBire: dataEdit.nameofVesselBire || '',
            vesselIdentifierTypeBire: dataEdit.vesselIdentifierTypeBire || '',
            vesselIdentifierBire: dataEdit.vesselIdentifierBire || '',
            typeOfvesselBire: dataEdit.typeOfVesselBire || '',
        };
        return obj;
    }

    const generateDataAddVessel = () => {
        const data = new FormData();
        data.append('ShipOwnerId', dataAddVessel.onBehalfCompany || '');
        data.append('IMOREG', dataAddVessel.imoRegNumber.toString() || '');
        data.append('VesselName', dataAddVessel.vesselName.trim() || '');
        data.append('Sirebire', dataAddVessel.type.includes('SIRE | BIRE') || '');
        data.append('Vetting', dataAddVessel.type.includes('Vetting') || '');
        data.append('SMCFile', dataAddVessel.smc || '');
        data.append('RegisterStatus', String(VESSEL_STATUS.ACTIVE));
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === 'Cancel Adding Vessel?') {
            onCloseVessel();
            return;
        }

        if (title === "Cancel Changes?") {
            history.back();
            return;
        }

        if (title === 'Add Vessel?') {
            handleConfirmVessel();
            return;
        }

        const obj = generateDataEdit() as any;
        let newObj = obj;

        if (dataEdit.agent) {
            newObj = { ...obj, agentId: dataEdit.agent };
        }

        let response;
        if (id){
            response = await putNomination(newObj);
        } else{
            response = await postNomination(newObj);
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Nomination saved.',
                description: "We've saved the nomination for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                history.back()
            }, 1000);
        } else {
            toast({
                title: 'Nomination failed to save.',
                description: "We could not save the nomination for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmVessel = async () => {
        if (title === "Cancel Adding Vessel?") {
            onCloseVessel();
            return;
        }

        const obj = generateDataAddVessel() as any;
        const response = await postVessel(obj) as any;
        setIsSubmitting(false);
        onCloseVessel();

        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Vessel created.',
                description: "We've created the vessel for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            getListShipName(selectedType);
        } else {
            toast({
                title: 'Vessel failed to create.',
                description: "We unabled to create vessel for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeCompany = (value: string, props: any) => {
        const rawData = listRawInspector.find((x: any) => x.inspectorId === value);
        setSelectedRawData(rawData);
        props.setFieldValue('companyName', value);
        props.setFieldValue('companyInspectionAddressDetail', rawData?.address);
        props.setFieldValue('companyInspectionPhoneDetail', rawData?.phone);
    }

    const handleChangeVessel = (value: string, props: any) => {
        const rawData = listRawShipName.find((x: any) => x.vesselId === value);
        props.setFieldValue('vesselIdentifier', rawData?.imoreg);
        props.setFieldValue('vesselIdentifierType', rawData?.vesselIdentifierType);
        props.setFieldValue('nameOfVessel', value);
    }

    const handleChangeVesselBire = (value: string, props: any) => {
        const rawData = listRawShipName.find((x: any) => x.vesselId === value);
        props.setFieldValue('vesselIdentifierBire', rawData?.imoreg);
        props.setFieldValue('vesselIdentifierTypeBire', rawData?.vesselIdentifierType);
        props.setFieldValue('nameofVesselBire', value);
    }

    const handleChangeFee = (value: string, props: any) => {
        const selectedData = listRawInspectionFee.find((x: any) => x.id === value);
        props.setFieldValue('groupLocation', value);
        props.setFieldValue('fee', selectedData.price);
        props.setFieldValue('awaitingDaysFee', selectedData.fee);
			setPrefix(selectedData.type === 'SIRE' ? 'USD' : 'IDR');
    }

    const handleChangeType = (props: any, value: string) => {
        setListInspectionFee([]);
        setPrefix('');
        props.setFieldValue('groupLocation', '');
        props.setFieldValue('awaitingDaysFee', 0);
        props.setFieldValue('companyType', value);
        props.setFieldValue('fee', 0);
        getDataListCompanyByType(value);

        let params = '';

        if (value === 'SIRE') {
            params = 'sire';
        } else if (value === 'BIRE') {
            params = 'sire';
        } else if (value === 'Vetting') {
            params = 'vetting';
        } else {
            params = '';
        }

        setSelectedType(params);

        getListInspectionFee(params);
        getListShipName(params);
    }

    const handleChangeAgent = (props: any, e: any) => {
        if (e === null) {
            setSelectedAgent('');
            props.setFieldValue('agent', '');
            props.setFieldValue('agentName', '');
            props.setFieldValue('agentOfficePhone', '');
            props.setFieldValue('agentMobilePhone', '');
            props.setFieldValue('agentFax', '');
            props.setFieldValue('agentEmailAddress', '');
            setIsAgentDisabled(false);
            return;
        }

        const dataAgent = listRawAgent?.find((x: any) => x.id === e.value);
        setSelectedAgent(e.value);
        props.setFieldValue('agent', e.value);
        props.setFieldValue('agentName', dataAgent ? dataAgent.name : '');
        props.setFieldValue('agentOfficePhone', dataAgent ? dataAgent.officePhone : '');
        props.setFieldValue('agentMobilePhone', dataAgent ? dataAgent.mobilePhone : '');
        props.setFieldValue('agentFax', dataAgent ? dataAgent.fax : '');
        props.setFieldValue('agentEmailAddress', dataAgent ? dataAgent.email : '');

        setIsAgentDisabled(!!dataAgent);
    }

    const handleAddNewVessel = () => {
        onOpenVessel();
    }

    const getListShipName = async (type: string) => {
        const response = await getDataVessel(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawShipName(response.data);
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        }
    }

    const getListAgent = async () => {
        const response = await getDataAgent() as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawAgent(response.data);
            const formattedOptions = response.data.map((item: any) => ({ value: item.id, label: item.name }));
            setListAgent(formattedOptions);
        } else {
            setListRawAgent([]);
            setListAgent([]);
        }
    }

    const getListInspectionFee = async (value: string) => {
        const response = await getInspectorFees(value) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawInspectionFee(response.data);
            const formattedData = response.data.map((item: any) => ({
                label: item.name,
                value: item.id,
            }));

            setListInspectionFee(formattedData);
        } else {
            setListRawInspectionFee([]);
            setListInspectionFee([]);
        }
    }

    const getDataListCompanyByType = async (value: string, init = false, id = '') => {
        const params = {
            sire: (value === 'SIRE'),
            bire: (value === 'BIRE'),
            vetting: (value === 'Vetting'),
        };

        const response = await getDataInspectorByType(params) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawInspector(response.data);
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorId,
                label: item.inspectorName,
            }));
            setListInspectorCompany(formattedData);
            if (init) {
                const rawData = response.data.find((x: any) => x.inspectorId === id);
                setDefaultCompanyInspectionAddressDetail(rawData.address);
                setDefaultCompanyInspectionPhoneDetail(rawData.phone);
            }
        } else {
            toast({
                title: 'Failed to get data inspectors.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorCompany([]);
        }
    }

    const getDataListInspectorPerson = async (type: string) => {
        const response = await getInspectorPersonData(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorPersonId,
                label: item.inspectorPersonName,
            }));
            setListInspectorPerson(formattedData);
        } else {
            toast({
                title: 'Failed to get data inspector person.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorPerson([]);
        }
    }

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            } else if (type === 'siretype') {
                setListSireType(formattedData);
            } else if (type === 'cargo') {
                setListCargo(formattedData);
            } else if (type === 'meansofaccess') {
                setListMeansOfAccess(formattedData);
            } else if (type === 'typeofvessel') {
                setListTypeOfVessels(formattedData);
            } else if (type === 'operation') {
                setListOperations(formattedData);
            }
        } else {
            toast({
                title: 'Failed to get data ref.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListMarket([]);
            setListSireType([]);
            setListCargo([]);
            setListMeansOfAccess([]);
            setListTypeOfVessels([]);
            setListOperations([]);
        }
    }

    const getDataDetail = async () => {
        const response = await getNominationById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            setApprovedBoth(response.data.nominationInspectorStatus === NOMINATION_STATUS.APPROVED
                && response.data.nominationShipOperatorStatus === NOMINATION_STATUS.APPROVED);
            setDataDetail(response.data);
            const rawAgent = listRawAgent.find((x: any) => x.id === response.data.agentId);
            setDefaultRawAgent(rawAgent);
            setIsAgentDisabled(true);

            const rawVessel = listRawShipName.find((x: any) => x.vesselId === response.data.vesselId);
            setDefaultRawVesselIdentifier(rawVessel);

            if (response.data.sire) {
                await Promise.all([
                    getDataListInspectorPerson('sire'),
                    getListShipName('sire'),
                ]);
                setDefaultType('SIRE');
                setSelectedType('sire');
                getListInspectionFee('sire');
                getDataListCompanyByType('sire', true, response.data.inspectingCompanyId);
							setPrefix('USD');
            }

            if (response.data.bire) {
                await Promise.all([
                    getDataListInspectorPerson('sire'),
                    getListShipName('bire'),
                ]);
                setDefaultType('BIRE');
                setSelectedType('sire');
                getListInspectionFee('sire');
                getDataListCompanyByType('bire', true, response.data.inspectingCompanyId);
							setPrefix('USD');
            }

            if (response.data.vetting) {
                await Promise.all([
                    getDataListInspectorPerson('vetting'),
                    getListShipName('vetting'),
                ])
                setDefaultType('Vetting');
                setSelectedType('vetting');
                getListInspectionFee('vetting');
                getDataListCompanyByType('vetting', true, response.data.inspectingCompanyId);
							setPrefix('IDR');
            }

            setIsLoading(false);
        } else {
            setIsLoading(false);
            toast({
                title: 'Failed to load nomination data.',
                description: response.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    getListShipName(''),
                    getListRef('typeofvessel'),
                    getListRef('operation'),
                    getListRef('market'),
                    getListRef('siretype'),
                    getListRef('cargo'),
                    getListRef('meansofaccess'),
                    getListAgent(),
                    getDataListInspectorPerson(''),
                ])

                setIsFirstEffectDone(true);
            } catch (_error) {
                console.log(_error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (isFirstEffectDone){
            if (id) {
                getDataDetail(); // this function also calls setIsLoading(false)
            } else {
                setIsLoading(false);
            }
        }
    }, [id, isFirstEffectDone]);

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalAddNewVessel isOpen={isOpenVessel} onClose={onCloseVessel} handleConfirmToCancel={handleCancelVessel} handleSubmitVessel={handleSubmitVessel} isSubmitting={isSubmitting} />
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                companyType: dataDetail ? defaultType : '',
                                companyName: dataDetail ? dataDetail.inspectingCompanyId : '',
                                companyInspectionAddressDetail: defaultCompanyInspectionAddressDetail,
                                companyInspectionPhoneDetail: defaultCompanyInspectionPhoneDetail,
                                inspectionDate: dataDetail ? dataDetail.inspectionDate : '',
                                location: dataDetail ? dataDetail.country : '',
                                requestDate: dataDetail ? dataDetail.requestDate : '',
                                market: dataDetail ? dataDetail.market : '',
                                nominationType: dataDetail ? dataDetail.type : '',
                                agent: dataDetail ? dataDetail.agentId : '',
                                agentName: defaultRawAgent ? defaultRawAgent.name : '',
                                agentOfficePhone: defaultRawAgent ? defaultRawAgent.officePhone : '',
                                agentMobilePhone: defaultRawAgent ? defaultRawAgent.mobilePhone : '',
                                agentFax: defaultRawAgent ? defaultRawAgent.fax : '',
                                agentEmailAddress: defaultRawAgent ? defaultRawAgent.email : '',
                                nameOfVessel: dataDetail ? dataDetail.vesselId : '',
                                vesselIdentifierType: dataDetail ? defaultRawVesselIdentifier.vesselIdentifierType : '',
                                vesselIdentifier: dataDetail ? defaultRawVesselIdentifier.imoreg : '',
                                typeOfVessel: dataDetail ? dataDetail.typeOfvessel : '',
                                ETAVessel: dataDetail ? dataDetail.vesselETA : '',
                                portOfBerth: dataDetail ? dataDetail.portOfBerth : '',
                                countryOfInspection: dataDetail ? dataDetail.countryOfInspection : '',
                                portOfInspection: dataDetail ? dataDetail.portOfInspection : '',
                                ETA: dataDetail ? dataDetail.eta : '',
                                ETB: dataDetail ? dataDetail.etb : '',
                                ETD: dataDetail ? dataDetail.etd : '',
                                operation: dataDetail ? dataDetail.operation : '',
                                cargo: dataDetail ? dataDetail.cargo : '',
                                meansOfAccess: dataDetail ? dataDetail.meansOfAccess : '',
                                inspectorName: dataDetail ? dataDetail.inspectorId : '',
                                codeBook: dataDetail ? dataDetail.codeBook : '',
                                groupLocation: dataDetail ? dataDetail.inspectionFeeId : '',
                                fee: dataDetail ? dataDetail.fee : '',
                                awaitingDaysFee: dataDetail ? dataDetail.awaitingDaysFee : '',
                                typeOfVesselBire: dataDetail ? dataDetail.typeOfvesselBire : '',
                                nameofVesselBire: dataDetail ? dataDetail.vesselIdBire : '',
                                vesselIdentifierTypeBire: dataDetail ? dataDetail.vesselIdentifierTypeBire : '',
                                vesselIdentifierBire: dataDetail ? dataDetail.vesselIdentifierBire : '',
                                
                            }}
                            validationSchema={() => inspectionNominationSchema(selectedAgent)}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='companyType'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.companyType && form.touched.companyType)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCompanyFullType name='companyType' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => handleChangeType(props, value)} value={props.values.companyType} defaultValue={props.values.companyType} hasError={form.errors.companyType && form.touched.companyType} />
                                                        <FormErrorMessage>{String(form.errors.companyType)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyName'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.companyName && form.touched.companyName)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='companyName' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => handleChangeCompany(value, props)} value={props.values.companyName} defaultValue={props.values.companyName} options={listInspectorCompany} hasError={form.errors.companyName && form.touched.companyName} />
                                                        <FormErrorMessage>{String(form.errors.companyName)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyInspectionAddressDetail'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.companyInspectionAddressDetail && form.touched.companyInspectionAddressDetail)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company Inspection Detail</FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} className="form-input" value={props.values.companyInspectionAddressDetail} disabled />
                                                        <FormErrorMessage>{String(form.errors.companyInspectionAddressDetail)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyInspectionPhoneDetail'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.companyInspectionPhoneDetail && form.touched.companyInspectionPhoneDetail)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label"></FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} className="form-input" value={props.values.companyInspectionPhoneDetail} disabled />
                                                        <FormErrorMessage>{String(form.errors.companyInspectionPhoneDetail)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectionDate'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.inspectionDate && form.touched.inspectionDate)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspection Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className={form.errors.inspectionDate && form.touched.inspectionDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="inspectionDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('inspectionDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Inspection Date"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{Boolean(form.errors.inspectionDate)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='location'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.location && form.touched.location)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Location<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='location' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('location', value)} value={props.values.location} hasError={form.errors.location && form.touched.location} defaultValue={props.values.location} />
                                                        <FormErrorMessage>{String(form.errors.location)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider mb={5} />
                                    <Field name='requestDate'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.requestDate && form.touched.requestDate)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Request Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className={form.errors.requestDate && form.touched.requestDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="requestDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('requestDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Request Date"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{Boolean(form.errors.requestDate)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='market'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.market && form.touched.market)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Market<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='market' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('market', value)} value={props.values.market} hasError={form.errors.market && form.touched.market} defaultValue={props.values.market} options={listMarket} />
                                                        <FormErrorMessage>{String(form.errors.market)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='nominationType'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.nominationType && form.touched.nominationType)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='nominationType' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('nominationType', value)} value={props.values.nominationType} hasError={form.errors.nominationType && form.touched.nominationType} defaultValue={props.values.nominationType} options={listSireType} />
                                                        <FormErrorMessage>{String(form.errors.nominationType)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Agent Details</Heading>
                                    <Divider mb={5} />
                                    <Field name='agent'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agent && form.touched.agent)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectAgent name='agent' isDisabled={isSubmitting || isApprovedBoth} handleChange={(e: any) => handleChangeAgent(props, e)} value={props.values.agent} hasError={form.errors.agent && form.touched.agent} defaultValue={props.values.agent} options={listAgent} />
                                                        <FormErrorMessage>{String(form.errors.agent)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentName'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agentName && form.touched.agentName)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Name' className="form-input" disabled={isAgentDisabled || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.agentName)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentOfficePhone'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agentOfficePhone && form.touched.agentOfficePhone)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Office Phone<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Office Phone' className="form-input" disabled={isAgentDisabled || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.agentOfficePhone)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentMobilePhone'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agentMobilePhone && form.touched.agentMobilePhone)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Mobile Phone<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Mobile Phone' className="form-input" disabled={isAgentDisabled || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.agentMobilePhone)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentFax'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agentFax && form.touched.agentFax)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Fax</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Fax' className="form-input" disabled={isAgentDisabled || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.agentFax)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentEmailAddress'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.agentEmailAddress && form.touched.agentEmailAddress)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Email Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Email Address' className="form-input" disabled={isAgentDisabled || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.agentEmailAddress)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Vessel Details</Heading>
                                    <Divider mb={5} />
                                    <Button mb={5} onClick={handleAddNewVessel} isDisabled={isSubmitting || isApprovedBoth}>Add New Vessel</Button>
                                    <Field name='nameOfVessel'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.nameOfVessel && form.touched.nameOfVessel)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Name of Vessel<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectShip
                                                            name='nameOfVessel'  placeholder="Choose Vessel"
                                                            handleChange={({ value }: any) => handleChangeVessel(value, props)}
                                                            value={props.values.nameOfVessel} defaultValue={{value: props.values.nameOfVessel, label: ''}}
                                                            options={listShipName}
                                                            hasError={Boolean(form.errors.nameOfVessel && form.touched.nameOfVessel)}
                                                            isDisabled={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage>{String(form.errors.nameOfVessel)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselIdentifierType'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.vesselIdentifierType && form.touched.vesselIdentifierType)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Identifier Type</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Vessel Identifier Type' className="form-input" disabled />
                                                        <FormErrorMessage>{String(form.errors.vesselIdentifierType)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselIdentifier'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.vesselIdentifier && form.touched.vesselIdentifier)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Identifier</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Vessel Identifier' className="form-input" disabled />
                                                        <FormErrorMessage>{String(form.errors.vesselIdentifier)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='typeOfVessel'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.typeOfVessel && form.touched.typeOfVessel)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type of Vessel<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectVesselType name='typeOfVessel' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('typeOfVessel', value)} value={props.values.typeOfVessel} hasError={form.errors.typeOfVessel && form.touched.typeOfVessel} defaultValue={props.values.typeOfVessel} options={listTypeOfVessels} />
                                                        <FormErrorMessage>{String(form.errors.typeOfVessel)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {/* BIRE Vessel Details */}
                                    {props.values.companyType.toLowerCase() === 'bire' && (
                                            <>
                                                {/* <Field name='nameofVesselBire'>
                                                    {({ field, form }: FieldProps) => (
                                                        <FormControl isInvalid={Boolean(form.errors.nameofVesselBire && form.touched.nameofVesselBire)} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Name of Vessel (Bire)</FormLabel>
                                                                <div className="form-input-container">
                                                                    <Input {...field} placeholder='Input Name of Vessel (Bire)' className="form-input" />
                                                                    <FormErrorMessage>{String(form.errors.nameofVesselBire)}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field> */}
                                                 <Field name='nameofVesselBire'>
                                                    {({ form }: FieldProps) => (
                                                        <FormControl isInvalid={Boolean(form.errors.nameofVesselBire && form.touched.nameofVesselBire)} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Name of Vessel (Bire) </FormLabel>
                                                                <div className="form-input-container">
                                                                    <SelectShip
                                                                        name='nameofVesselBire'  placeholder="Choose Vessel"
                                                                        handleChange={({ value }: any) => handleChangeVesselBire(value, props)}
                                                                        value={props.values.nameofVesselBire} defaultValue={{value: props.values.nameofVesselBire, label: ''}}
                                                                        options={listShipName}
                                                                        hasError={Boolean(form.errors.nameofVesselBire && form.touched.nameofVesselBire)}
                                                                        isDisabled={isSubmitting || isApprovedBoth}
                                                                    />
                                                                    <FormErrorMessage>{String(form.errors.nameofVesselBire)}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                                
                                                <Field name='vesselIdentifierTypeBire'>
                                                    {({ field, form }: FieldProps) => (
                                                        <FormControl isInvalid={Boolean(form.errors.vesselIdentifierTypeBire && form.touched.vesselIdentifierTypeBire)} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Vessel Identifier Type (Bire)</FormLabel>
                                                                <div className="form-input-container">
                                                                    <Input {...field} placeholder='Input Vessel Identifier Type (Bire)' className="form-input" />
                                                                    <FormErrorMessage>{String(form.errors.vesselIdentifierTypeBire)}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                                <Field name='vesselIdentifierBire'>
                                                    {({ field, form }: FieldProps) => (
                                                        <FormControl isInvalid={Boolean(form.errors.vesselIdentifierBire && form.touched.vesselIdentifierBire)} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Vessel Identifier (Bire)</FormLabel>
                                                                <div className="form-input-container">
                                                                    <Input {...field} placeholder='Input Vessel Identifier (Bire)' className="form-input"  />
                                                                    <FormErrorMessage>{String(form.errors.vesselIdentifierBire)}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                                <Field name='typeOfVesselBire'>
                                                    {({ form }: FieldProps) => (
                                                        <FormControl isInvalid={Boolean(form.errors.typeOfVesselBire && form.touched.typeOfVesselBire)} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Type of Vessel (Bire)</FormLabel>
                                                                <div className="form-input-container">
                                                                    <SelectVesselType name='typeOfVesselBire'  handleChange={({ value }: any) => props.setFieldValue('typeOfVesselBire', value)} value={props.values.typeOfVesselBire} hasError={form.errors.typeOfVesselBire && form.touched.typeOfVesselBire} defaultValue={props.values.typeOfVesselBire} options={listTypeOfVessels} />
                                                                    <FormErrorMessage>{String(form.errors.typeOfVesselBire)}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                            </>
                                        )
                                    }
                                   
                                    {/* <Field name='ETAVessel'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.ETAVessel && form.touched.ETAVessel)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETA<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className={form.errors.ETAVessel && form.touched.ETAVessel ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETAVessel"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETAVessel', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETA"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.ETAVessel)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field> */}
                                    {/* <Field name='portOfBerth'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.portOfBerth && form.touched.portOfBerth)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Port of Berth<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Port of Berth' className="form-input" disabled={isSubmitting || isApprovedBoth} />
                                                        <FormErrorMessage>{String(form.errors.portOfBerth)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field> */}
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Inspection Details</Heading>
                                    <Divider mb={5} />
                                    <Field name='countryOfInspection'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.countryOfInspection && form.touched.countryOfInspection)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Country of Inspection<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='countryOfInspection' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('countryOfInspection', value)} value={props.values.countryOfInspection} hasError={form.errors.countryOfInspection && form.touched.countryOfInspection} defaultValue={props.values.countryOfInspection} />
                                                        <FormErrorMessage>{String(form.errors.countryOfInspection)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='portOfInspection'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.portOfInspection && form.touched.portOfInspection)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Port of Inspection<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Port of Inspection' className="form-input" disabled={isSubmitting || isApprovedBoth}/>
                                                        <FormErrorMessage>{String(form.errors.portOfInspection)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETA'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.ETA && form.touched.ETA)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETA<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className={form.errors.ETA && form.touched.ETA ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETA"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETA', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETA"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.ETA)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETB'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.ETB && form.touched.ETB)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETB</FormLabel>
                                                    <div className={form.errors.ETB && form.touched.ETB ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETB"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETB', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETB"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.ETB)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETD'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.ETD && form.touched.ETD)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETD<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className={form.errors.ETD && form.touched.ETD ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETD"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETD', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETD"
                                                            readOnly={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.ETD)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='operation'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.operation && form.touched.operation)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Operation<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectOperationType name='operation' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('operation', value)} value={props.values.operation} hasError={form.errors.operation && form.touched.operation} defaultValue={props.values.operation} options={listOperations} />
                                                        <FormErrorMessage>{String(form.errors.operation)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='cargo'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.cargo && form.touched.cargo)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Cargo<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='cargo' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('cargo', value)} value={props.values.cargo} hasError={form.errors.cargo && form.touched.cargo} defaultValue={props.values.cargo} options={listCargo} />
                                                        <FormErrorMessage>{String(form.errors.cargo)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='meansOfAccess'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.meansOfAccess && form.touched.meansOfAccess)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Means of Access<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='meansOfAccess' isDisabled={isSubmitting || isApprovedBoth} handleChange={({ value }: any) => props.setFieldValue('meansOfAccess', value)} value={props.values.meansOfAccess} hasError={form.errors.meansOfAccess && form.touched.meansOfAccess} defaultValue={props.values.meansOfAccess} options={listMeansOfAccess} />
                                                        <FormErrorMessage>{String(form.errors.meansOfAccess)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectorName'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.inspectorName && form.touched.inspectorName)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspector Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='inspectorName' isDisabled={isSubmitting} handleChange={({ value }: any) => props.setFieldValue('inspectorName', value)} value={props.values.inspectorName} defaultValue={props.values.inspectorName} options={listInspectorPerson} hasError={form.errors.inspectorName && form.touched.inspectorName} />
                                                        <FormErrorMessage>{String(form.errors.inspectorName)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='codeBook'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.codeBook && form.touched.codeBook)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Code Book</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Code Book' className="form-input" disabled={isSubmitting || isApprovedBoth}/>
                                                        <FormErrorMessage>{String(form.errors.codeBook)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Inspection Fee</Heading>
                                    <Divider mb={5} />
                                    <Field name='groupLocation'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.groupLocation && form.touched.groupLocation)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Group Location<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspectionFee
                                                            name='groupLocation'
                                                            placeholder="Choose Type"
                                                            options={listInspectionFee}
                                                            handleChange={({ value }: { value: string }) => handleChangeFee(value, props)}
                                                            value={form.values.groupLocation}
                                                            defaultValue={form.values.groupLocation}
                                                            haserror={Boolean(form.errors.groupLocation && form.touched.groupLocation)}
                                                            isDisabled={isSubmitting || isApprovedBoth}
                                                        />
                                                        <FormErrorMessage>{String(form.errors.groupLocation)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='fee'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.fee && form.touched.fee)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspection Fee</FormLabel>
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Inspection Fee'
                                                        prefix={prefix}
                                                        value={props.values.fee}
                                                        error={Boolean(form.errors.fee && form.touched.fee)}
                                                        onValueChange={({ floatValue = 0 }) => props.setFieldValue('fee', floatValue)}
                                                        width="100%"
                                                        disabled
                                                    />
                                                    <FormErrorMessage>{String(form.errors.fee)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='awaitingDaysFee'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.awaitingDaysFee && form.touched.awaitingDaysFee)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Awaiting Days Fee</FormLabel>
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Awaiting Days Fee'
                                                        prefix={prefix}
                                                        value={props.values.awaitingDaysFee}
                                                        error={Boolean(form.errors.awaitingDaysFee && form.touched.awaitingDaysFee)}
                                                        onValueChange={({ floatValue = 0 }) => props.setFieldValue('awaitingDaysFee', floatValue)}
                                                        width="100%"
                                                        disabled
                                                    />
                                                    <FormErrorMessage>{String(form.errors.awaitingDaysFee)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default EditNomination;
